import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Category accent palette — each report group gets its own hue so the icon
// color itself tells you what kind of report you're looking at.
const ACCENTS = {
  fleet: { base: "#F2A93B", soft: "#FDF1DD" },     // Fleet & Vehicle
  location: { base: "#1E9E77", soft: "#E3F5EE" },  // Location & Geofencing
  trips: { base: "#4C5FD5", soft: "#E9EBFB" },     // Trips, Stops & Routes
  distance: { base: "#8B5FBF", soft: "#F1E9F9" },  // Distance & Attendance
  alerts: { base: "#E1574F", soft: "#FBE7E6" },    // Alerts & Media
};

function ReportCard({ icon, title, category, onClick }) {
  const accent = ACCENTS[category] || ACCENTS.fleet;

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: "pointer",
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        borderRadius: "14px",
        border: "1px solid #E7E9EE",
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
        padding: "14px 10px 12px",
        overflow: "hidden",
        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 12px 24px -8px ${accent.base}55`,
          borderColor: accent.base,
        },
        "&:hover .report-card-icon": {
          transform: "scale(1.06)",
        },
        "&:hover .report-card-chevron": {
          opacity: 1,
          transform: "translateX(0)",
        },
        // top accent bar
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: accent.base,
          opacity: 0,
          transition: "opacity 0.18s ease",
        },
        "&:hover::before": {
          opacity: 1,
        },
      }}
    >
      <MDBox
        className="report-card-chevron"
        sx={{
          position: "absolute",
          top: 8,
          right: 10,
          fontSize: "0.95rem",
          color: accent.base,
          opacity: 0,
          transform: "translateX(4px)",
          transition: "opacity 0.18s ease, transform 0.18s ease",
        }}
      >
        <Icon fontSize="small">arrow_outward</Icon>
      </MDBox>

      <MDBox
        className="report-card-icon"
        sx={{
          display: "grid",
          placeItems: "center",
          width: "38px",
          height: "38px",
          borderRadius: "10px",
          background: accent.soft,
          color: accent.base,
          marginBottom: "8px",
          transition: "transform 0.18s ease",
          "& .material-icons": { fontSize: "1.15rem" },
        }}
      >
        <Icon fontSize="small">{icon}</Icon>
      </MDBox>

      <MDTypography
        variant="button"
        fontWeight="medium"
        sx={{
          fontFamily: '"Inter", "Roboto", sans-serif',
          fontSize: "0.72rem",
          color: "#1B2A4A",
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
        }}
      >
        {title}
      </MDTypography>
    </Card>
  );
}

ReportCard.defaultProps = {
  category: "fleet",
};

ReportCard.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  category: PropTypes.oneOf(["fleet", "location", "trips", "distance", "alerts"]),
  onClick: PropTypes.func,
};

export default ReportCard;